import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import CgDialog from 'modules/Common/ui/CgDialog';
import CgRadixButton from 'modules/Common/ui/CgRadixButton';
import { isClerkAdminRole } from 'modules/Common/utils/Utility';
import { notifyError } from 'modules/Common/ui/toast/api';
import { fetchVerifierMembers, setVerifierMemberRole } from '../api/organizations';

// verifier-firm-foundation phase 2 (R8): make or remove a verifier firm's Admin.
export default function FirmAdminsDialog({ organization, onClose }) {
  const queryClient = useQueryClient();
  const queryKey = ['adminVerifierMembers', organization._id];
  const { data: members = [], isLoading } = useQuery({
    queryKey,
    queryFn: () => fetchVerifierMembers(organization._id),
  });
  const mutation = useMutation({
    mutationFn: setVerifierMemberRole,
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
    onError: notifyError,
  });

  return (
    <CgDialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={`Firm admins — ${organization.name}`}
      description="The first person to use a firm's invite becomes its admin. Reassign here if that is not who the verification body wants."
      width={560}
      footer={
        <div className="flex justify-end">
          <CgRadixButton
            variant="secondary"
            label="Close"
            onClick={onClose}
          />
        </div>
      }
    >
      {isLoading ? (
        <p className="text-sm text-slate-500">Loading members…</p>
      ) : members.length === 0 ? (
        <p className="text-sm text-slate-500">This firm has no members yet.</p>
      ) : (
        <ul className="m-0 list-none divide-y divide-slate-100 p-0">
          {members.map((m) => (
            <li
              key={m.clerkUserId}
              className="flex items-center gap-3 py-2"
            >
              <div className="min-w-0 flex-1">
                <p className="m-0 truncate text-sm text-slate-900">{m.name || m.email}</p>
                <p className="m-0 truncate text-xs text-slate-500">{m.email}</p>
              </div>
              <CgRadixButton
                size="sm"
                variant={isClerkAdminRole(m.role) ? 'secondary' : 'primary'}
                label={isClerkAdminRole(m.role) ? 'Remove admin' : 'Make admin'}
                loading={mutation.isPending && mutation.variables?.clerkUserId === m.clerkUserId}
                onClick={() =>
                  mutation.mutate({
                    id: organization._id,
                    clerkUserId: m.clerkUserId,
                    role: isClerkAdminRole(m.role) ? 'org:member' : 'org:admin',
                  })
                }
              />
            </li>
          ))}
        </ul>
      )}
    </CgDialog>
  );
}
